import Phaser from 'phaser';
import { buildOptionsMenuItems } from '@/data/menuConfig';
import { MenuNavigation } from '@/systems/MenuNavigation';
import { type PauseMenuState } from '@/systems/PauseMenuState';
import {
  adjustOption,
  applyLiveAudio,
  applyOptionConfirm,
  optionValueText,
  type OptionActionResult,
} from '@/systems/OptionsMenuActions';

const FONT = '"Courier New", monospace';
const DEFAULT_DEPTH = 1001;

export class OptionsMenuForm {
  private readonly nav = new MenuNavigation(buildOptionsMenuItems());
  private readonly depth: number;
  private group!: Phaser.GameObjects.Group;
  private labelTexts: Phaser.GameObjects.Text[] = [];
  private valueTexts: Phaser.GameObjects.Text[] = [];
  private cursor!: Phaser.GameObjects.Text;
  private notifText!: Phaser.GameObjects.Text;
  private notifTimer?: Phaser.Time.TimerEvent;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly state: PauseMenuState,
    depth = DEFAULT_DEPTH,
  ) {
    this.depth = depth;
  }

  build(width: number, height: number): void {
    this.group = this.scene.add.group();

    const title = this.scene.add
      .text(width / 2, height * 0.18, 'OPTIONS', {
        fontFamily: FONT,
        fontSize: '38px',
        color: '#ef233c',
        stroke: '#000000',
        strokeThickness: 5,
      })
      .setOrigin(0.5)
      .setDepth(this.depth);
    this.group.add(title);

    const startY = height * 0.3;
    const step = 48;
    const labelX = width / 2 - 120;
    const valueX = width / 2 + 140;

    this.labelTexts = this.nav.items.map((item, i) => {
      const t = this.scene.add
        .text(labelX, startY + i * step, item.label, {
          fontFamily: FONT,
          fontSize: '22px',
          color: '#888888',
        })
        .setOrigin(0, 0.5)
        .setDepth(this.depth);
      this.group.add(t);
      return t;
    });

    this.valueTexts = this.nav.items.map((_item, i) => {
      const t = this.scene.add
        .text(valueX, startY + i * step, '', {
          fontFamily: FONT,
          fontSize: '22px',
          color: '#ffd700',
        })
        .setOrigin(1, 0.5)
        .setDepth(this.depth);
      this.group.add(t);
      return t;
    });

    this.cursor = this.scene.add
      .text(0, 0, '▶', {
        fontFamily: FONT,
        fontSize: '18px',
        color: '#ef233c',
      })
      .setOrigin(1, 0.5)
      .setDepth(this.depth);
    this.group.add(this.cursor);

    this.notifText = this.scene.add
      .text(width / 2, height * 0.9, '', {
        fontFamily: FONT,
        fontSize: '18px',
        color: '#ffba08',
        stroke: '#000000',
        strokeThickness: 3,
      })
      .setOrigin(0.5)
      .setDepth(this.depth)
      .setAlpha(0);
    this.group.add(this.notifText);

    this.syncCursor();
    this.refreshValues();
  }

  setVisible(visible: boolean): void {
    this.group.setVisible(visible);
  }

  moveUp(): void {
    this.nav.moveUp();
    this.syncCursor();
  }

  moveDown(): void {
    this.nav.moveDown();
    this.syncCursor();
  }

  adjust(delta: number): OptionActionResult {
    const result = adjustOption(this.state, this.nav.current.id, delta);
    this.afterAction(result);
    return result;
  }

  confirm(): OptionActionResult {
    const result = applyOptionConfirm(this.state, this.nav.confirm());
    this.afterAction(result);
    return result;
  }

  refreshValues(): void {
    const opts = this.state.opts;
    this.nav.items.forEach((item, i) => {
      const t = this.valueTexts[i];
      if (t) {
        t.setText(optionValueText(item.id, opts));
      }
    });
  }

  private afterAction(result: OptionActionResult): void {
    if (result.audioChanged) {
      applyLiveAudio(this.state.opts);
    }
    if (result.changed) {
      this.refreshValues();
    }
    if (result.notification) {
      this.showNotif(result.notification);
    }
  }

  private syncCursor(): void {
    const idx = this.nav.index;
    this.labelTexts.forEach((t, i) => {
      t.setColor(i === idx ? '#ffffff' : '#888888');
    });
    this.valueTexts.forEach((t, i) => {
      t.setColor(i === idx ? '#ffd700' : '#666633');
    });
    const sel = this.labelTexts[idx];
    if (sel) {
      this.cursor.setPosition(sel.x - 18, sel.y);
    }
  }

  private showNotif(msg: string): void {
    if (this.notifTimer) {
      this.notifTimer.remove();
    }
    this.notifText.setText(msg).setAlpha(1);
    this.notifTimer = this.scene.time.delayedCall(1800, () => {
      this.scene.tweens.add({
        targets: this.notifText,
        alpha: 0,
        duration: 400,
        ease: 'Quad.easeIn',
      });
    });
  }
}
